// Manager-only Ops page: manual price overrides for the pricing waterfall.
// The resolver checks this layer FIRST — used for SAFTs, depegs, off-market
// instruments where the public sources don't have a usable price.

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, type ReactNode } from 'react';

import { Kicker, PageHeader } from '../../components/shell';
import { api } from '../../lib/api';

interface Override {
  id: string;
  asset: string;
  fund_id: string | null;
  fund_name: string | null;
  price: string;
  base_currency: string;
  effective_from: string;
  effective_to: string | null;
  source_note: string;
  has_attachment: boolean;
  set_by_email: string;
  superseded_by_id: string | null;
  created_at: string;
}

interface FundOverview { id: string; name: string }

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

export default function PriceOverrides() {
  const [rows, setRows] = useState<Override[] | null>(null);
  const [funds, setFunds] = useState<FundOverview[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api<Override[]>('/ops/price-overrides')
      .then(d => { if (!cancelled) setRows(d); })
      .catch((e: any) => { if (!cancelled) setErr(e?.payload?.detail || 'Failed to load.'); });
    api<FundOverview[]>('/ops/funds')
      .then(d => { if (!cancelled) setFunds(d); })
      .catch(() => { /* funds list is optional */ });
    return () => { cancelled = true; };
  }, [reloadKey]);

  return (
    <div className="view container">
      <PageHeader
        kicker="OPS · PRICE OVERRIDES"
        title={<><em>Manual</em> price overrides.</>}
        meta={<span className="label-s">RESOLVER LAYER 1 — BEATS COINGECKO + CEX</span>}
      />

      <p className="serif-body" style={{ color: 'var(--ink-2)', maxWidth: '60ch', marginBottom: 32 }}>
        Use for SAFTs, off-market instruments, and depeg events. Operator-set
        prices apply before any public source in the pricing waterfall. Append-only
        — rotate by creating a new override and superseding the predecessor.
      </p>

      <CreateForm funds={funds} onCreated={() => setReloadKey(k => k + 1)} />

      <hr className="rule" style={{ margin: '32px 0' }} />

      {err && <p className="serif-body" style={{ color: 'var(--accent)' }}>{err}</p>}
      {rows === null && !err && <p className="label-s" style={{ color: 'var(--ink-3)' }}>Loading…</p>}
      {rows && rows.length === 0 && (
        <p className="serif-body" style={{ color: 'var(--ink-2)', fontStyle: 'italic' }}>
          No active overrides. The waterfall is running on the public sources only.
        </p>
      )}
      {rows && rows.length > 0 && (
        <div style={{ borderTop: '1px solid var(--ink)' }}>
          {rows.map((r, i) => (
            <div
              key={r.id}
              style={{
                padding: '18px 0',
                borderBottom: i === rows.length - 1 ? '1px solid var(--ink)' : 'var(--hairline)',
                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 2fr auto', gap: 24, alignItems: 'baseline',
              }}
            >
              <span className="serif" style={{ fontSize: 18 }}>{r.asset}</span>
              <span className="num">{r.price} {r.base_currency}</span>
              <span className="label-s">
                {r.fund_name ? `FUND · ${r.fund_name.toUpperCase()}` : 'TENANT-WIDE'}
              </span>
              <span className="label-s" style={{ color: 'var(--ink-3)' }}>
                FROM {fmtDateTime(r.effective_from)}
                {r.effective_to ? ` · TO ${fmtDateTime(r.effective_to)}` : ''}
                {r.source_note ? ` · ${r.source_note}` : ''}
              </span>
              <span className="label-s" style={{ color: 'var(--ink-3)' }}>
                BY {r.set_by_email}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateForm({ funds, onCreated }: { funds: FundOverview[]; onCreated: () => void }) {
  const [asset, setAsset] = useState('');
  const [price, setPrice] = useState('');
  const [ccy, setCcy] = useState('USD');
  const [fundId, setFundId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: any) => {
    e.preventDefault(); setBusy(true); setMsg(null); setErr(null);
    try {
      await api('/ops/price-overrides', {
        method: 'POST',
        body: {
          asset, price, base_currency: ccy,
          fund_id: fundId || undefined,
          source_note: note,
        },
      });
      setAsset(''); setPrice(''); setNote(''); setFundId('');
      setMsg(`Override saved for ${asset.toUpperCase()}.`);
      onCreated();
    } catch (e: any) {
      setErr(e?.payload?.detail || 'Failed to save override.');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} style={{
      padding: 18, border: 'var(--hairline)', borderColor: 'var(--rule)',
      display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 2fr auto', gap: 16, alignItems: 'end',
    }}>
      <Field label="ASSET">
        <input className="field-input" required value={asset}
               onChange={e => setAsset(e.target.value.toUpperCase())} placeholder="BTC" />
      </Field>
      <Field label="PRICE">
        <input className="field-input num" required value={price}
               onChange={e => setPrice(e.target.value)} placeholder="50000.00" />
      </Field>
      <Field label="CURRENCY">
        <input className="field-input" value={ccy} maxLength={8}
               onChange={e => setCcy(e.target.value.toUpperCase())} />
      </Field>
      <Field label="FUND">
        <select className="field-input" value={fundId}
                onChange={e => setFundId(e.target.value)}>
          <option value="">(tenant-wide)</option>
          {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </Field>
      <Field label="NOTE">
        <input className="field-input" value={note}
               onChange={e => setNote(e.target.value)}
               placeholder="SAFT, depeg, manual reseed…" />
      </Field>
      <button className="btn" disabled={busy || !asset || !price}>
        {busy ? 'Saving…' : 'Save →'}
      </button>
      {err && <span className="label-s" style={{ color: 'var(--oxblood, #8a3030)', gridColumn: '1 / -1' }}>{err}</span>}
      {msg && <span className="label-s" style={{ color: 'var(--accent)', gridColumn: '1 / -1' }}>{msg}</span>}
    </form>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="label-s" style={{ display: 'block', marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}
